import type { JSONContent } from "@tiptap/core";

export const convertToParagraphs = (content: string | JSONContent): JSONContent | null => {
  if (!content) return null;
  // If it's already a Tiptap JSON object
  if (typeof content === "object" && content.type === "doc") {
    // Return as-is if it already looks like proper Tiptap JSON with marks
    // (this preserves diff marks from diffToTiptap)
    // But still convert if there are unsupported node types like headings
    const hasMarks = content.content?.some(node =>
      node.content?.some(child => child.marks && child.marks.length > 0)
    );
    const hasUnsupportedNodes = content.content?.some(node =>
      node.type === "heading"
    );

    if (hasMarks && !hasUnsupportedNodes) {
      return content;
    }

    // Check if we need to convert any text nodes with newlines
    const newContent = content.content
      ?.map((node) => {
        // Convert headings to paragraphs (the editor doesn't load the Heading extension)
        if (node.type === "heading") {
          return { ...node, type: "paragraph", attrs: undefined };
        }
        if (node.type === "paragraph" && node.content?.[0]?.type === "text" && node.content[0].text?.includes("\n")) {
          // Split the text by newlines and create paragraph nodes
          const paragraphs = node.content[0].text.split("\n").map((text) => ({
            type: "paragraph",
            content: text.trim() ? [{ type: "text", text }] : [],
          }));
          return paragraphs;
        }
        return node;
      })
      .flat();

    return {
      type: "doc",
      content: newContent,
    };
  }

  if (typeof content === "string") {
    const rawLines = content.replace(/\r\n?/g, "\n").split("\n");
    let start = 0;
    let end = rawLines.length - 1;
    while (start <= end && rawLines[start].trim().length === 0) start += 1;
    while (end >= start && rawLines[end].trim().length === 0) end -= 1;

    if (start > end) {
      return {
        type: "doc",
        content: [{ type: "paragraph", content: [] }],
      };
    }

    const paragraphs: JSONContent[] = [];
    let currentParagraphLines: string[] = [];
    let emptyLineRun = 0;
    for (let i = start; i <= end; i += 1) {
      const line = rawLines[i];
      if (line.trim().length === 0) {
        if (currentParagraphLines.length > 0) {
          const lineNodes: JSONContent[] = [];
          currentParagraphLines.forEach((paragraphLine, index) => {
            if (paragraphLine.trim().length > 0) {
              lineNodes.push({ type: "text", text: paragraphLine });
            }
            if (index < currentParagraphLines.length - 1) {
              lineNodes.push({ type: "hardBreak" });
            }
          });
          paragraphs.push({
            type: "paragraph",
            content: lineNodes,
          });
          currentParagraphLines = [];
        }
        emptyLineRun += 1;
        continue;
      }

      if (emptyLineRun > 1) {
        for (let p = 0; p < emptyLineRun - 1; p += 1) {
          paragraphs.push({ type: "paragraph", content: [] });
        }
      }
      emptyLineRun = 0;
      currentParagraphLines.push(line);
    }

    if (currentParagraphLines.length > 0) {
      const lineNodes: JSONContent[] = [];
      currentParagraphLines.forEach((line, index) => {
        if (line.trim().length > 0) {
          lineNodes.push({ type: "text", text: line });
        }
        if (index < currentParagraphLines.length - 1) {
          lineNodes.push({ type: "hardBreak" });
        }
      });

      paragraphs.push({
        type: "paragraph",
        content: lineNodes,
      });
    }

    return {
      type: "doc",
      content: paragraphs.length ? paragraphs : [{ type: "paragraph", content: [] }],
    };
  }

  return null;
};
